import { useEffect, useRef } from 'preact/hooks';
import * as ScheduleX from '@schedule-x/calendar';
import { createDragAndDropPlugin } from '@schedule-x/drag-and-drop';
import { createResizePlugin } from '@schedule-x/resize';
import { createScrollControllerPlugin } from '@schedule-x/scroll-controller';
import { createEventModalPlugin } from '@schedule-x/event-modal';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { createCalendar, viewMonthGrid, viewMonthAgenda, viewWeek, viewDay } = ScheduleX;
export function useCalendarInstance({ loading, resolvedTheme, onEventUpdate, calendarAppRef, canEdit }) {
    const calendarRef = useRef(null);
    const onEventUpdateRef = useRef(onEventUpdate);
    // Keep the ref updated with the latest callback
    useEffect(() => {
        onEventUpdateRef.current = onEventUpdate;
    }, [onEventUpdate]);
    useEffect(() => {
        if (loading || !calendarRef.current || calendarAppRef.current)
            return;
        try {
            // Only enable drag-and-drop and resize if user has edit permission
            const plugins = [];
            if (canEdit) {
                const dragAndDrop = createDragAndDropPlugin(15); // 15 minute intervals
                const resize = createResizePlugin(15); // 15 minute intervals
                plugins.push(dragAndDrop, resize);
            }
            // Add event modal plugin
            const eventModal = createEventModalPlugin();
            plugins.push(eventModal);
            // Calculate initial scroll to 1 hour before current time
            const now = new Date();
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            const hours = oneHourAgo.getHours().toString().padStart(2, '0');
            const minutes = oneHourAgo.getMinutes().toString().padStart(2, '0');
            const initialScrollTime = `${hours}:${minutes}`;
            const scrollController = createScrollControllerPlugin({
                initialScroll: initialScrollTime
            });
            plugins.push(scrollController);
            const calendar = createCalendar({
                views: [viewMonthGrid, viewMonthAgenda, viewWeek, viewDay],
                defaultView: viewMonthGrid.name,
                events: [],
                plugins,
                calendars: {
                    'email': {
                        colorName: 'email',
                        lightColors: {
                            main: '#3b82f6',
                            container: '#dbeafe',
                            onContainer: '#1e40af',
                        },
                        darkColors: {
                            main: '#60a5fa',
                            container: '#1e3a8a',
                            onContainer: '#bfdbfe',
                        },
                    },
                    'phone': {
                        colorName: 'phone',
                        lightColors: {
                            main: '#10b981',
                            container: '#d1fae5',
                            onContainer: '#047857',
                        },
                        darkColors: {
                            main: '#34d399',
                            container: '#064e3b',
                            onContainer: '#a7f3d0',
                        },
                    },
                    'social': {
                        colorName: 'social',
                        lightColors: {
                            main: '#f59e0b',
                            container: '#fef3c7',
                            onContainer: '#b45309',
                        },
                        darkColors: {
                            main: '#fbbf24',
                            container: '#78350f',
                            onContainer: '#fde68a',
                        },
                    },
                    'online-event': {
                        colorName: 'online-event',
                        lightColors: {
                            main: '#8b5cf6',
                            container: '#ede9fe',
                            onContainer: '#6d28d9',
                        },
                        darkColors: {
                            main: '#a78bfa',
                            container: '#4c1d95',
                            onContainer: '#ddd6fe',
                        },
                    },
                    'launch-date': {
                        colorName: 'launch-date',
                        lightColors: {
                            main: '#06b6d4',
                            container: '#cffafe',
                            onContainer: '#0e7490',
                        },
                        darkColors: {
                            main: '#22d3ee',
                            container: '#164e63',
                            onContainer: '#a5f3fc',
                        },
                    },
                },
                isDark: resolvedTheme === 'dark',
                callbacks: {
                    onEventUpdate: (event) => {
                        if (!canEdit) {
                            return;
                        }
                        onEventUpdateRef.current(event);
                    },
                },
            });
            calendar.render(calendarRef.current);
            calendarAppRef.current = calendar;
            // Apply dark theme class to the Schedule-X calendar wrapper if in dark mode
            const applyDarkTheme = () => {
                if (resolvedTheme === 'dark' && calendarRef.current) {
                    const calendarWrapper = calendarRef.current.querySelector('.sx__calendar-wrapper');
                    if (calendarWrapper && !calendarWrapper.classList.contains('is-dark')) {
                        calendarWrapper.classList.add('is-dark');
                    }
                }
            };
            // Apply initially
            applyDarkTheme();
            // Watch for DOM changes (view switches) and reapply dark theme
            let observer = null;
            if (calendarRef.current && resolvedTheme === 'dark') {
                observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                            applyDarkTheme();
                        }
                        else if (mutation.type === 'childList') {
                            applyDarkTheme();
                        }
                    });
                });
                const calendarWrapper = calendarRef.current.querySelector('.sx__calendar-wrapper');
                if (calendarWrapper) {
                    observer.observe(calendarWrapper, {
                        attributes: true,
                        attributeFilter: ['class'],
                        childList: true,
                        subtree: false
                    });
                }
            }
            // Cleanup function for HMR and unmount
            return () => {
                if (observer) {
                    observer.disconnect();
                }
                // Always clear the calendar ref to allow recreation
                if (calendarRef.current) {
                    calendarRef.current.innerHTML = '';
                }
                calendarAppRef.current = null;
            };
        }
        catch (error) {
            console.error('Error creating/rendering calendar:', error);
        }
    }, [loading, resolvedTheme, canEdit]);
    return calendarRef;
}
